import { useEffect, useRef, useState, type ReactNode, useCallback } from 'react';
import { useBackForward } from '../../useBackForward';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { Button, Callout, Flex, RadioCards, Text } from '@dodilio/ui-design-lib';
import { auth, myOrganizations, restoreSession, signIn, signOut as endSession } from '../../auth';
import { ServiceRegister } from '../../api';
import { HeroShell } from '../../components/HeroShell';
import { PageMeta } from '../../components/PageMeta';
import { createAccountAtom } from '../../state';
import { ClientInvitation, MyOrganization } from '../../types/register';
import {
    forgetClientInvite, inviteErrorText, inviteState, inviteStateText, noteClientInvite, readClientInvite,
} from '../../clientInvite';
import { forgetReferral } from '../../referral';
import { isTransient, isWrongAccount } from '../../apiError';

/*
 * A partner invited this person's company to Dodil as its client. Accepting
 * makes their organization the partner's REFERRED client: it keeps its own
 * Dodil account and billing, and the partner can work with it.
 *
 *   signed out -> create an account (email locked when iam told us which)
 *                 or sign in; both come back here or through the org step
 *   signed in  -> the invitation must be for this email; pick one of the
 *                 organizations they hold (or create the first), accept
 */

const Pane = ({ partnerName }: { partnerName?: string }) => (
    <>
        <h1 style={{ fontSize: 'clamp(22px, 2.6vw, 28px)', fontWeight: 500, lineHeight: 1.06, letterSpacing: '-0.03em', margin: '0 0 18px', color: 'var(--gray-12)' }}>
            {partnerName ? `Work with ${partnerName} on Dodil.` : 'Work with your partner on Dodil.'}
        </h1>
        <Text as="p" size="2" className="heroPaneDesc" style={{ color: 'var(--gray-11)', margin: '0 0 22px', lineHeight: 1.5 }}>
            Your organization keeps its own Dodil account and billing. {partnerName || 'Your partner'} builds and runs your systems on it, and you see every invoice.
        </Text>
    </>
);

type View =
    | { kind: 'checking' }
    | { kind: 'missing' }
    | { kind: 'signedOut'; invite?: ClientInvitation }
    | { kind: 'closed'; text: string }
    | { kind: 'refused'; text: string; retry?: boolean; signOut?: boolean }
    | { kind: 'choose'; invite: ClientInvitation; orgs: MyOrganization[]; token: string }
    | { kind: 'done'; partnerName: string };

export const ClientInvite = () => {
    const nav = useNavigate();
    const setAccount = useSetAtom(createAccountAtom);
    const [stored] = useState(readClientInvite);
    const [view, setView] = useState<View>({ kind: stored ? 'checking' : 'missing' });
    const [orgId, setOrgId] = useState<string>('');
    const [busy, setBusy] = useState(false);
    // Back from Keycloak or the console: the buttons work again.
    useBackForward(useCallback(() => setBusy(false), []));
    const [error, setError] = useState('');
    const redirect = import.meta.env.VITE_DEFAULT_REDIRECT;

    // Once only: the first authenticate() spends Keycloak's sign-in code, so a
    // second run (StrictMode) would find no session.
    const started = useRef(false);
    useEffect(() => {
        if (!stored || started.current) return;
        started.current = true;
        const decide = async () => {
            // Non-interactive: a new client has no account yet.
            let bearer: string | undefined;
            try { if ((await restoreSession()) === 'signed-in') bearer = auth.getAccessToken(); } catch { /* signed out */ }

            let invite: ClientInvitation | undefined;
            try {
                invite = await ServiceRegister.getClientInvitation(stored.token, bearer);
            } catch (e) {
                const status = (e as { response?: { status?: number } })?.response?.status;
                // Signed out, iam may not describe the invitation: offer both doors.
                if (!bearer && (status === 401 || status === 403)) { setView({ kind: 'signedOut' }); return; }
                setView({ kind: 'refused', text: inviteErrorText(e, stored.email), retry: isTransient(e), signOut: !!bearer && isWrongAccount(e) });
                return;
            }
            noteClientInvite({ email: invite.email, partnerName: invite.partnerName, clientName: invite.clientName });

            const closed = inviteStateText(inviteState(invite.status, invite.expiresAt), invite.partnerName, !!bearer);
            if (closed) { setView({ kind: 'closed', text: closed }); return; }
            if (!bearer) { setView({ kind: 'signedOut', invite }); return; }

            let orgs: MyOrganization[];
            try {
                orgs = await myOrganizations();
            } catch {
                // Never read as "none": that would send them to make another organization.
                setView({ kind: 'refused', text: 'We couldn\'t check your organizations just now. Try again in a moment.', retry: true });
                return;
            }
            const held = orgs.filter((o) => o.isCreator);
            if (held.length === 0) {
                // Nothing they can accept for yet: the org step accepts once it exists.
                nav('/signup/org?new=1');
                return;
            }
            setOrgId(held.length === 1 ? held[0].organizationId : '');
            setView({ kind: 'choose', invite, orgs: held, token: bearer });
        };
        decide().catch((e) => {
            console.warn('[register] client invitation', e);
            setView({ kind: 'refused', text: inviteErrorText(e, stored.email), retry: true });
        });
    }, [nav, stored]);

    // Off to the console a moment after accepting, unless they leave first.
    useEffect(() => {
        if (view.kind !== 'done') return;
        const t = window.setTimeout(() => { window.location.href = redirect; }, 2500);
        return () => window.clearTimeout(t);
    }, [view.kind, redirect]);

    const signUp = () => {
        const email = view.kind === 'signedOut' ? view.invite?.email : undefined;
        if (email) setAccount({ email, password: '' });
        nav('/');
    };
    const signInHere = () => signIn(window.location.origin + '/client-invite');
    // Out, then back to this invitation as whoever signs in next.
    const signOut = async () => { try { await endSession(`${window.location.origin}/client-invite`); } catch { /* already out */ } };

    const accept = async () => {
        if (view.kind !== 'choose' || !stored || !orgId) return;
        setBusy(true);
        setError('');
        try {
            await ServiceRegister.acceptClientInvitation(stored.token, orgId, view.token);
            forgetClientInvite();
            forgetReferral();
            await auth.forceRefresh().catch(() => false);
            setView({ kind: 'done', partnerName: view.invite.partnerName || 'your partner' });
        } catch (e) {
            setError(inviteErrorText(e, view.invite.email));
            setBusy(false);
        }
    };

    let body: ReactNode;
    switch (view.kind) {
        case 'checking':
            body = <Text size="2" role="status">Checking your invitation…</Text>;
            break;
        case 'missing':
            body = (
                <Callout.Root color="red" size="2" role="alert">
                    <Callout.Text>This page needs the link from your invitation email. Open the email and use its link.</Callout.Text>
                </Callout.Root>
            );
            break;
        case 'closed':
        case 'refused':
            body = (
                <Flex direction="column" gap="3">
                    <Callout.Root color={view.kind === 'closed' ? 'amber' : 'red'} size="2" role="alert">
                        <Callout.Text>{view.text}</Callout.Text>
                    </Callout.Root>
                    {view.kind === 'refused' && view.signOut && (
                        <Button radius="full" size="2" variant="soft" onClick={signOut}>Sign out</Button>
                    )}
                    {view.kind === 'refused' && view.retry && (
                        // A Keycloak instance starts once per page: trying again is a reload.
                        <Button radius="full" size="2" variant="soft" onClick={() => window.location.reload()}>Try again</Button>
                    )}
                </Flex>
            );
            break;
        case 'signedOut':
            body = (
                <Flex direction="column" gap="3">
                    <Text size="2">
                        {view.invite?.partnerName
                            ? <><strong>{view.invite.partnerName}</strong> invited {view.invite.clientName ? <strong>{view.invite.clientName}</strong> : 'you'} to Dodil.</>
                            : 'A Dodil partner invited you to Dodil.'}
                    </Text>
                    <Text size="1" color="gray">
                        {view.invite?.email
                            ? <>Use {view.invite.email}, the address the invitation was sent to.</>
                            : 'Use the email address the invitation was sent to.'}
                    </Text>
                    <Button radius="full" size="2" variant="classic" onClick={signUp}>Create an account</Button>
                    <Button radius="full" size="2" variant="soft" onClick={signInHere}>I have a Dodil account: sign in</Button>
                </Flex>
            );
            break;
        case 'choose':
            body = (
                <Flex direction="column" gap="3">
                    {error && (
                        <Callout.Root color="red" size="1" role="alert"><Callout.Text>{error}</Callout.Text></Callout.Root>
                    )}
                    <Text size="2">
                        <strong>{view.invite.partnerName || 'A Dodil partner'}</strong> invited {view.invite.clientName ? <strong>{view.invite.clientName}</strong> : 'you'} to work with them on Dodil.
                    </Text>
                    <Text size="1" color="gray" id="org-pick-label">
                        {view.orgs.length > 1 ? 'Which organization is this for? Pick one that doesn\'t already work with a partner.' : 'This organization will work with them:'}
                    </Text>
                    <RadioCards.Root value={orgId} onValueChange={setOrgId} columns="1" aria-labelledby="org-pick-label">
                        {view.orgs.map((o) => (
                            <RadioCards.Item key={o.organizationId} value={o.organizationId}>
                                <Text size="2">{o.organizationName}</Text>
                            </RadioCards.Item>
                        ))}
                    </RadioCards.Root>
                    <Text size="1" color="gray">
                        It keeps its own billing; {view.invite.partnerName || 'the partner'} can then send you quotes and contracts, and work in it once you agree.
                    </Text>
                    <Button radius="full" size="2" variant="classic" disabled={!orgId || busy} loading={busy} onClick={accept}>
                        Accept the invitation
                    </Button>
                    {/* Every org they hold may already work with a partner (CompleteSignup makes another). */}
                    <Button radius="full" size="2" variant={/already works with a partner/.test(error) ? 'soft' : 'ghost'} disabled={busy} onClick={() => nav('/signup/org?new=1')}>
                        Create a new organization for this
                    </Button>
                </Flex>
            );
            break;
        case 'done':
            body = (
                <Callout.Root color="green" size="2" role="status">
                    <Callout.Text>You&apos;re now working with {view.partnerName} on Dodil. Taking you to the console…</Callout.Text>
                </Callout.Root>
            );
            break;
    }

    const partnerName = view.kind === 'choose' ? view.invite.partnerName : view.kind === 'signedOut' ? view.invite?.partnerName
        : view.kind === 'done' ? view.partnerName : stored?.partnerName;
    return (
        <>
            <PageMeta title="Accept your invitation · Dodil Cloud" description="A Dodil partner invited your organization to work with them on Dodil." />
            <HeroShell title="Your invitation" leftPane={<Pane partnerName={partnerName} />}>
                {body}
            </HeroShell>
        </>
    );
};
